import React, { FC, useState, ChangeEvent, useMemo } from 'react';
import {
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogContent,
  Menu,
  MenuItem,
  Select,
  useMediaQuery,
  useTheme,
  Tooltip,
} from '@material-ui/core';
import AssessmentIcon from '@material-ui/icons/Assessment';
import PeopleIcon from '@material-ui/icons/People';
import AppsIcon from '@material-ui/icons/Apps';
import { GridColumns, DataGrid, GridCellParams, GridSortDirection, GridRowParams } from '@material-ui/data-grid';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import { DateRange } from 'materialui-daterange-picker';
import { DateRangePicker } from 'components/DateRangePicker';
import { todayEnd, todayStart } from 'components/DateRangePicker/DefaultRanges';
import { Search } from 'components/Search';
import { handleCompanyUrl } from 'utils/organizationUtils';
import { toFormatDateTime } from 'utils';
import { useStyles, BootstrapInput } from './styles';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import TabPanel from 'components/TabPanel';
import Calender from '../../assets/icons/EndUser/Calender.svg';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const toDateParam = (date: Date) => {
  const y = date.getFullYear();
  const m = `${date.getMonth() + 1}`.padStart(2, '0');
  const d = `${date.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const OIDC: FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [activeTab, setActiveTab] = useState('one');
  const [period, setPeriod] = useState('year');
  const [dateRange, setDateRange] = useState<DateRange>({ startDate: todayStart, endDate: todayEnd });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRows, setTotalRows] = useState(0);
  const [open, setOpen] = useState(false);
  const [selectedDescription, setSelectedDescription] = useState<string | React.ReactNode>('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const startDate = toDateParam(dateRange.startDate as Date);
  const endDate = toDateParam(dateRange.endDate as Date);

  function a11yProps(index: string) {
    return { id: `wrapped-tab-${index}`, 'aria-controls': `wrapped-tabpanel-${index}` };
  }

  const {
    logsTableStore: { getOidcReports, viewUser, viewEndUser, showAllDateRange },
    userStore: { user },
    notificationStore: { openNotification },
  } = useStore();

  const getClickedUser = useMutation(
    'viewUserOidc',
    (id: number) => (user?.permission !== 'EndUser' ? viewUser(id) : viewEndUser(id)),
    {
      onSuccess: (res) => {
        let url;
        if (res?.data?.message === 'End User Group and Department') {
          url = `${handleCompanyUrl('/admin/profile')}`;
          window.open(url, '_blank');
        }
        localStorage.setItem('ClickedUser', JSON.stringify(res.data.user));
        if (res?.data?.message === 'User found') {
          url = `${handleCompanyUrl('/user/view')}`;
        } else {
          url = `${handleCompanyUrl('/admin/profile')}`;
        }
        window.open(url, '_blank');
      },
      onError: (error: any) => {
        openNotification('error', error.data?.message || error.message || 'User not found');
      },
    }
  );

  const getOidcReportsReq = useQuery(
    ['getOidcReportsReq', period, startDate, endDate, page, pageSize, searchTerm],
    () => getOidcReports(period, startDate, endDate),
    {
      keepPreviousData: true,
      onSuccess: (data: any) => {
        const total = data?.data?.logs?.total || 0;
        setTotalRows(total);
      },
      onError: () => openNotification('error', `Can't Get OIDC Reports`),
    }
  );

  useAppPreloader(getOidcReportsReq.isFetching || getClickedUser.isLoading);

  const authData = getOidcReportsReq.data?.data?.authentications_by_period?.data || [];
  const uniqueUsersData = getOidcReportsReq.data?.data?.unique_users_by_period?.data || [];
  const connectedAppsData = getOidcReportsReq.data?.data?.connected_apps?.data || [];
  const logData: any[] = useMemo(() => {
    const raw = getOidcReportsReq.data?.data?.logs?.data || [];
    return raw.map((row: any) => ({
      id: row.id,
      createdAt: row.created_at || '',
      description: row.description || '',
      ip: row.properties?.ip || row.properties?.ip_address || 'N/A',
      city: row.properties?.city || '',
      country: row.properties?.country || '',
      subject_id: row.subject_id || null,
      user_id: row.user_id || row.subject_id || null,
    }));
  }, [getOidcReportsReq.data]);

  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return logData;
    const s = searchTerm.toLowerCase();
    return logData.filter((r) =>
      `${r.createdAt} ${r.description} ${r.ip}`.toLowerCase().includes(s)
    );
  }, [logData, searchTerm]);

  const pagedRows = useMemo(() => {
    return filteredRows.slice(page * pageSize, page * pageSize + pageSize);
  }, [filteredRows, page, pageSize]);

  const handleExportPDF = () => {
    const doc = new jsPDF();
    doc.text('OIDC Report', 70, 20);
    (doc as any).autoTable({
      head: [['Date and Time', 'Description']],
      body: logData.map((r) => [toFormatDateTime(r.createdAt), r.description]),
      startY: 25,
    });
    doc.save('oidc-report.pdf');
  };

  const handleExportCSV = () => {
    let csv = 'Date and Time,Description\n';
    logData.forEach((r) => { csv += `${toFormatDateTime(r.createdAt)},${r.description}\n`; });
    const link = document.createElement('a');
    link.setAttribute('href', URL.createObjectURL(new Blob([csv], { type: 'text/csv' })));
    link.setAttribute('download', 'oidc-report.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportXLS = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ['Date and Time', 'Description'],
      ...logData.map((r) => [toFormatDateTime(r.createdAt), r.description]),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'OIDC Report');
    XLSX.writeFile(wb, 'oidc-report.xlsx');
  };

  const emailRegex = /([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const emailTest = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const renderDescription = (value: string, subjectId: number | null) => {
    const truncated = value.length > (isMobile ? 50 : 100) ? value.substring(0, isMobile ? 50 : 100) + '...' : value;

    const emailMatch = value.match(/([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/);
    const userIdMatch = value.match(/\(ID:\s*([^)]+)\)/);
    const userNameMatch = value.match(/User:\s*([^\s,]+)/) || value.match(/\sfor\s+([^\s]+)\s+by\s/);
    const groupCreatedMatch = value.match(/Group created \(Name: ([^)]+)\)/);
    const deptCreatedMatch = value.match(/Department created \(Name: ([^)]+)\)/);

    const renderWithLinks = (msg: string) => {
      const parts: React.ReactNode[] = [];
      let last = 0;

      const elements: { index: number; end: number; text: string; onClick: (e: any) => void }[] = [];

      if (emailMatch) {
        const idx = msg.indexOf(emailMatch[0]);
        if (idx !== -1) elements.push({ index: idx, end: idx + emailMatch[0].length, text: emailMatch[0], onClick: (e) => { e.stopPropagation(); if (subjectId) getClickedUser.mutate(subjectId); } });
      }
      if (userIdMatch) {
        const full = `(ID: ${userIdMatch[1]})`;
        const idx = msg.indexOf(full);
        if (idx !== -1) elements.push({ index: idx, end: idx + full.length, text: full, onClick: (e) => { e.stopPropagation(); if (subjectId) getClickedUser.mutate(subjectId); } });
      }
      if (userNameMatch && !emailMatch && !userIdMatch) {
        const name = userNameMatch[1];
        const idx = msg.indexOf(name);
        if (idx !== -1) elements.push({ index: idx, end: idx + name.length, text: name, onClick: (e) => { e.stopPropagation(); if (subjectId) getClickedUser.mutate(subjectId); } });
      }

      elements.sort((a, b) => a.index - b.index);
      elements.forEach((el) => {
        if (el.index > last) parts.push(msg.substring(last, el.index));
        parts.push(<span key={el.index} style={{ color: '#1976d2', fontWeight: 500, cursor: 'pointer' }} onClick={el.onClick}>{el.text}</span>);
        last = el.end;
      });
      if (last < msg.length) parts.push(msg.substring(last));
      return parts;
    };

    return (
      <div style={{ display: 'flex', width: '100%' }}>
        <Typography noWrap style={{ fontSize: isMobile ? '11px' : '14px' }}>{renderWithLinks(truncated)}</Typography>
        {value.length > (isMobile ? 50 : 100) && (
          <Button style={{ color: 'blue', fontSize: isMobile ? '10px' : '12px', minWidth: 'auto' }}
            onClick={() => { setSelectedDescription(<div style={{ fontSize: '14px', lineHeight: '1.5' }}>{renderWithLinks(value)}</div>); setOpen(true); }}>
            {isMobile ? 'More' : 'Read more...'}
          </Button>
        )}
      </div>
    );
  };

  const columns: GridColumns = [
    { field: 'id', headerName: 'ID', hide: true, width: 100 },
    {
      field: 'createdAt', headerName: 'DATE AND TIME', flex: 1,
      renderHeader: () => (
        <span style={{ fontSize: isMobile ? '10px' : '14px', fontWeight: 'bold' }}>DATE AND TIME</span>
      ),
      renderCell: (params: GridCellParams) => (
        <Tooltip title={toFormatDateTime(params.row.createdAt)} placement='bottom'>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', overflow: 'hidden', whiteSpace: 'nowrap' }}>
            <img src={Calender} alt='' style={{ width: '16px', height: '16px' }} />
            <span style={{ fontSize: isMobile ? '11px' : '13px' }}>{toFormatDateTime(params.row.createdAt)}</span>
          </div>
        </Tooltip>
      ),
    },
    {
      field: 'description', headerName: 'DESCRIPTION', flex: 2.2,
      renderHeader: () => (
        <span style={{ fontSize: isMobile ? '10px' : '14px', fontWeight: 'bold' }}>DESCRIPTION</span>
      ),
      renderCell: (params: GridCellParams) => renderDescription(params.row.description, params.row.user_id),
    },
    // {
    //   field: 'ip', headerName: 'LOCATION', flex: 1.5,
    //   renderHeader: () => (
    //     <span style={{ fontSize: isMobile ? '10px' : '14px', fontWeight: 'bold' }}>LOCATION</span>
    //   ),
    //   renderCell: (params: GridCellParams) => (
    //     <span style={{ fontSize: isMobile ? '11px' : '13px' }}>
    //       {params.row.ip}{params.row.city ? `, ${params.row.city}` : ''}{params.row.country ? `, ${params.row.country}` : ''}
    //     </span>
    //   ),
    // },
  ];

  return (
    <>
      <Grid container justify='space-between' alignItems='center' className={classes.box}>
        <div className={classes.flex}>
          <DateRangePicker
            initialRange={dateRange}
            customRanges={[{ label: 'Show All', startDate: showAllDateRange.startDate, endDate: showAllDateRange.endDate }]}
            onChange={(r) => { setDateRange(r); setPage(0); }}
          />
          <Select
            value={period}
            onChange={(e) => { setPeriod(e.target.value as string); setPage(0); }}
            displayEmpty
            input={<BootstrapInput />}
          >
            <MenuItem value='week'>Week</MenuItem>
            <MenuItem value='month'>Month</MenuItem>
            <MenuItem value='year'>Year</MenuItem>
          </Select>
        </div>
      </Grid>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} className={classes.tabs}
        TabIndicatorProps={{ style: { color: 'transparent', background: 'transparent' } }} variant='standard'>
        <Tab value='one' label='Authenticate' {...a11yProps('one')} className={classes.label} />
        <Tab value='two' label='Unique Users' {...a11yProps('two')} className={classes.label} />
        <Tab value='three' label='Connected Apps' {...a11yProps('three')} className={classes.label} />
      </Tabs>

      <div className={classes.tabBox}>
        {/* TAB 1 - AUTHENTICATE */}
        <TabPanel value={activeTab} index='one'>
          <Grid container spacing={3}>
            {authData.length > 0 ? authData.map((item: any, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <AssessmentIcon className={classes.statsIcon} style={{ color: '#1976d2' }} />
                    <Typography className={classes.statsValue}>{Number(item.authentication_count || 0).toLocaleString()}</Typography>
                    <Typography className={classes.statsLabel}>Authentications - {item.period}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Card className={classes.statsCard}>
                  <CardContent><Typography align='center'>No authentication data available</Typography></CardContent>
                </Card>
              </Grid>
            )}
          </Grid>

          {/* Logs Table */}
          <Grid container justify='space-between' alignItems='center' style={{ marginTop: 24, marginBottom: 8 }}>
            <div style={{ width: isMobile ? '100%' : isTablet ? '250px' : '300px' }}>
              <Search handelSearch={(f: string) => { setSearchTerm(f); setPage(0); }} />
            </div>
            <div>
              <Button onClick={(e) => setAnchorEl(e.currentTarget)}
                style={{ color: '#4682B4', fontSize: isMobile ? '12px' : '14px' }}>
                <DownloadIcon style={{ color: '#4682B4', fontSize: '20px', marginRight: '4px' }} />
                Exports
              </Button>
              <Menu anchorEl={anchorEl} keepMounted open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)} style={{ top: '45px' }}>
                <MenuItem onClick={() => { handleExportCSV(); setAnchorEl(null); }}>Download as CSV</MenuItem>
                <MenuItem onClick={() => { handleExportXLS(); setAnchorEl(null); }}>Download as Excel</MenuItem>
                <MenuItem onClick={() => { handleExportPDF(); setAnchorEl(null); }}>Download as PDF</MenuItem>
              </Menu>
            </div>
          </Grid>

          <Paper elevation={0} style={{ width: '100%', overflowX: 'auto' }}>
            <div style={{ minWidth: isMobile ? 900 : '100%' }}>
              <DataGrid
                autoHeight
                disableSelectionOnClick
                rows={pagedRows}
                columns={columns}
                pageSize={pageSize}
                rowsPerPageOptions={[10, 25, 50, 100]}
                pagination
                paginationMode='client'
                rowCount={filteredRows.length}
                page={page}
                onPageChange={(p) => setPage(p.page)}
                onPageSizeChange={(p) => { setPageSize(p.pageSize); setPage(0); }}
                sortingOrder={['desc', 'asc']}
                sortModel={[{ field: 'id', sort: 'desc' as GridSortDirection }]}
                density={isMobile ? 'compact' : 'standard'}
                className={classes.noBorder}
                getRowClassName={(params: GridRowParams) => `row-background-${params.row.type || 'default'}`}
              />
            </div>
          </Paper>
        </TabPanel>

        {/* TAB 2 - UNIQUE USERS */}
        <TabPanel value={activeTab} index='two'>
          <Grid container spacing={3}>
            {uniqueUsersData.length > 0 ? uniqueUsersData.map((item: any, index: number) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Card className={classes.statsCard}>
                  <CardContent>
                    <PeopleIcon className={classes.statsIcon} style={{ color: '#4caf50' }} />
                    <Typography className={classes.statsValue}>{Number(item.unique_user_count || 0).toLocaleString()}</Typography>
                    <Typography className={classes.statsLabel}>Unique Users - {item.period}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            )) : (
              <Grid item xs={12}>
                <Card className={classes.statsCard}>
                  <CardContent><Typography align='center'>No unique users data available</Typography></CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </TabPanel>

        {/* TAB 3 - CONNECTED APPS */}
        <TabPanel value={activeTab} index='three'>
          <Paper className={classes.tableContainer}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Connected Apps</strong></TableCell>
                    <TableCell align='center'><strong>Number of Unique Users</strong></TableCell>
                    <TableCell align='center'><strong>Number of Logins</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {connectedAppsData.length > 0 ? connectedAppsData.map((app: any) => (
                    <TableRow key={app.app_name}>
                      <TableCell component='th' scope='row'>{app.app_name}</TableCell>
                      <TableCell align='center'>{app.unique_user_count}</TableCell>
                      <TableCell align='center'>{app.login_count}</TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={3} align='center'>No connected apps data available</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </TabPanel>
      </div>

      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogContent dividers>{selectedDescription}</DialogContent>
      </Dialog>
    </>
  );
};

export default OIDC;
